import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { faSlidersSimple } from '@awesome.me/kit-f3dc60f0d0/icons/classic/regular';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans, useLingui } from '@lingui/react/macro';
import { Button, Input } from '@repo/videa-components';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { uniqBy } from 'lodash';
import { useInView } from 'react-intersection-observer';
import { useHistory } from 'react-router-dom';

import { VideaTypography } from '@/common-components/elements/VideaTypography/VideaTypography';
import { useAppointmentsFilteredByProviderAndOperatory } from '@/modules/patientList/helpers';
import type { AppointmentProvider } from '@/react-query/useAppointments';
import { useVoiceNoteTemplates } from '@/react-query/videa-notes/useVoiceNoteTemplates';
import routes from '@/routes/routes.constants';

import {
  ambientViewTypeAtom,
  jumpToCurrentApptAtom,
  operatoryListAtom,
  recentNotesViewAtom,
  searchAtom,
  selectDateAtom,
  templatesSearchAtom,
} from '../../patientList/atoms';
import { PatientListPageLayout } from '../../patientList/PatientListPageLayout';
import { PatientListPageLayoutContextMap } from '../../patientList/types';
import { getNextAppointmentByStandalonePatientVisitId } from '../../patientList/useScrollAppointmentIntoView';
import { TemplatesTable } from '../templatesPage/TemplatesTable';

import { AmbientTable } from './AmbientTable';
import { AmbientViewTabs } from './AmbientViewTabs';
import { useAmbientAppointmentsData } from './hooks/useAmbientAppointmentsData';
import { useAmbientInfiniteScroll } from './hooks/useAmbientInfiniteScroll';
import { useAmbientSorting } from './hooks/useAmbientSorting';

export const AmbientPage = () => {
  // atoms
  const [selectedDate, setSelectedDate] = useAtom(selectDateAtom);
  const [templatesSearch, setTemplatesSearch] = useAtom(templatesSearchAtom);
  const ambientViewType = useAtomValue(ambientViewTypeAtom);
  const jumpToCurrentAppt = useAtomValue(jumpToCurrentApptAtom);
  const search = useAtomValue(searchAtom);
  const isRecentNotesView = useAtomValue(recentNotesViewAtom);
  const setOperatoryList = useSetAtom(operatoryListAtom);

  // hooks
  const { t } = useLingui();
  const history = useHistory();
  const { inView: isNextAppointmentInView } = useInView();

  // refs
  const nextAppointmentRef = useRef<HTMLTableRowElement>(null);

  // Ambient-specific hooks (for Notes view)
  const {
    appointmentsWithNotes,
    isLoading,
    isError,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    hasPages,
    invalidateNotesCache,
    isUnifiedNotesLoading,
    isStandaloneNotesLoading,
  } = useAmbientAppointmentsData();

  const filteredAppointments = useAppointmentsFilteredByProviderAndOperatory(
    appointmentsWithNotes,
  );

  const { sortedAppointments, sortField, sortDirection, handleSort } =
    useAmbientSorting(filteredAppointments, isRecentNotesView);

  const providersList = useMemo(() => {
    const providers = appointmentsWithNotes
      ?.map(({ appointment }) => appointment.provider)
      .filter((a) => !!a);
    return uniqBy(providers, 'id') as AppointmentProvider[];
  }, [appointmentsWithNotes]);

  useEffect(() => {
    const newOperatoriesList = [
      ...new Set(
        (appointmentsWithNotes ?? [])
          .map(({ appointment }) => appointment.operatory?.name)
          .filter(Boolean),
      ),
    ];

    setOperatoryList(newOperatoriesList as string[]);
  }, [appointmentsWithNotes, setOperatoryList]);

  // Templates-specific hooks (for Templates view)
  const {
    data: templatesData = [],
    isLoading: isLoadingTemplates,
    isError: isTemplatesError,
  } = useVoiceNoteTemplates();

  // Templates sorting state
  const [templatesSortField, setTemplatesSortField] = useState('name');
  const [templatesSortDirection, setTemplatesSortDirection] = useState<
    'asc' | 'desc'
  >('asc');

  const filteredAndSortedTemplates = useMemo(() => {
    // First filter by templates search term
    const searchLower = templatesSearch.toLowerCase().trim();
    const filtered = searchLower
      ? templatesData.filter((template) =>
          template.name?.toLowerCase().includes(searchLower),
        )
      : templatesData;

    // Then sort
    const sorted = [...filtered];
    sorted.sort((a, b) => {
      let aVal: string = '';
      let bVal: string = '';

      switch (templatesSortField) {
        case 'name':
          aVal = a.name || '';
          bVal = b.name || '';
          break;
        case 'type':
          aVal = a.type || '';
          bVal = b.type || '';
          break;
        case 'creator':
          aVal = a.createdBy?.name || '';
          bVal = b.createdBy?.name || '';
          break;
        default:
          return 0;
      }

      const comparison = aVal.localeCompare(bVal);
      return templatesSortDirection === 'asc' ? comparison : -comparison;
    });
    return sorted;
  }, [
    templatesData,
    templatesSortField,
    templatesSortDirection,
    templatesSearch,
  ]);

  const handleTemplatesSort = (field: string, direction: 'asc' | 'desc') => {
    setTemplatesSortField(field);
    setTemplatesSortDirection(direction);
  };

  const nextAppointmentStandalonePatientVisitId =
    getNextAppointmentByStandalonePatientVisitId({
      appointments: sortedAppointments,
      selectedDate,
    });

  // Calculate indices using useMemo
  const { nextAppointmentIndex } = useMemo(() => {
    return {
      nextAppointmentIndex: sortedAppointments.findIndex(
        (appointment) =>
          appointment.appointment.standalonePatientVisitId ===
          nextAppointmentStandalonePatientVisitId,
      ),
    };
  }, [sortedAppointments, nextAppointmentStandalonePatientVisitId]);

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { ref: sentinelRef, setScrollRoot } = useAmbientInfiniteScroll(
    hasNextPage,
    isFetchingNextPage,
    hasPages,
    fetchNextPage,
  );
  const scrollContainerCallbackRef = useCallback(
    (node: HTMLDivElement | null) => {
      (scrollContainerRef as React.MutableRefObject<HTMLDivElement | null>).current =
        node;
      setScrollRoot(node);
    },
    [setScrollRoot],
  );

  const scrollNextAppointmentIntoView = useCallback(() => {
    if (nextAppointmentIndex >= 0) {
      nextAppointmentRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [nextAppointmentIndex, nextAppointmentRef]);

  // Set today as default date and reset when search is cleared
  // Skip this behavior when in recent notes view mode
  useEffect(() => {
    if (!isRecentNotesView && search === '' && !selectedDate) {
      setSelectedDate(new Date());
    }
  }, [search, selectedDate, setSelectedDate, isRecentNotesView]);

  useEffect(() => {
    if (jumpToCurrentAppt === 'true') {
      scrollNextAppointmentIntoView();
    }
  }, [jumpToCurrentAppt, scrollNextAppointmentIntoView]);

  return (
    <div className="vai-flex vai-flex-col vai-h-screen vai-w-screen vai-overflow-hidden">
      <AmbientViewTabs />

      {ambientViewType === 'templates' ? (
        <div className="vai-flex-1 vai-flex vai-flex-col vai-overflow-hidden">
          {/* Templates Topbar */}
          <div className="vai-bg-white vai-px-4 vai-shadow-sm vai-flex vai-items-center vai-justify-between vai-h-16">
            <div className="vai-w-80 vai-relative">
              <FontAwesomeIcon
                icon={faSearch}
                className="vai-absolute vai-left-3 vai-top-1/2 vai-transform vai--translate-y-1/2 vai-text-gray-400 vai-h-4 vai-w-4 vai-z-10"
              />
              <Input
                type="text"
                placeholder={t`Search templates`}
                value={templatesSearch}
                onChange={(e) => setTemplatesSearch(e.target.value)}
                className="vai-pl-10"
              />
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={() =>
                history.push(routes.AMBIENT_TEMPLATE_FIELD_SETTINGS)
              }
              className="vai-inline-flex vai-items-center vai-gap-2"
            >
              <FontAwesomeIcon
                icon={faSlidersSimple}
                className="vai-h-4 vai-w-4 vai-text-gray-600"
              />
              <Trans>Field Settings</Trans>
            </Button>
          </div>
          {/* Templates Content */}
          <div className="vai-flex-1 vai-flex vai-flex-col vai-bg-gray-100 vai-p-4 vai-overflow-hidden">
            {isTemplatesError ? (
              <div className="vai-flex vai-justify-center vai-items-center vai-p-8">
                <div className="vai-text-red-500">
                  <Trans>Error loading templates</Trans>
                </div>
              </div>
            ) : isLoadingTemplates ? (
              <div className="vai-flex vai-justify-center vai-items-center vai-p-8">
                <div className="vai-text-gray-600">
                  <Trans>Loading templates...</Trans>
                </div>
              </div>
            ) : filteredAndSortedTemplates.length === 0 ? (
              <VideaTypography
                variant="headingExtrasmall"
                className="vai-w-full vai-text-center vai-p-10 vai-text-zinc-700"
                as="h3"
              >
                {templatesSearch ? (
                  <Trans>No templates found matching your search.</Trans>
                ) : (
                  <Trans>No templates available.</Trans>
                )}
              </VideaTypography>
            ) : (
              <TemplatesTable
                templates={filteredAndSortedTemplates}
                onSort={handleTemplatesSort}
                sortField={templatesSortField}
                sortDirection={templatesSortDirection}
                scrollContainerRef={scrollContainerCallbackRef}
                sentinelRef={sentinelRef}
              />
            )}
          </div>
        </div>
      ) : (
        // Notes View - default
        <PatientListPageLayout
          context={PatientListPageLayoutContextMap.Ambient}
          providersList={providersList}
          allAppointments={sortedAppointments}
          isLoadingAppointments={isLoading}
          isLoadingVisitSummaries={false}
          nextAppointmentIndex={nextAppointmentIndex}
          isNextAppointmentInView={isNextAppointmentInView}
          onScrollNextAppointmentIntoView={scrollNextAppointmentIntoView}
        >
          <div className="vai-flex vai-flex-col vai-h-full vai-overflow-hidden">
            {isError ? (
              <div className="vai-flex vai-justify-center vai-items-center vai-p-8">
                <div className="vai-text-red-500">
                  <Trans>Error loading appointments</Trans>
                </div>
              </div>
            ) : (
              <div className="vai-flex vai-flex-1 vai-flex-col vai-min-h-0 vai-overflow-hidden">
                <AmbientTable
                  appointments={sortedAppointments}
                  onSort={handleSort}
                  sortField={sortField}
                  sortDirection={sortDirection}
                  invalidateNotesCache={invalidateNotesCache}
                  scrollContainerRef={scrollContainerCallbackRef}
                  sentinelRef={sentinelRef}
                  nextAppointmentRef={nextAppointmentRef}
                  isRecentNotesView={isRecentNotesView}
                  isUnifiedNotesLoading={isUnifiedNotesLoading}
                  isStandaloneNotesLoading={isStandaloneNotesLoading}
                />
              </div>
            )}
          </div>
        </PatientListPageLayout>
      )}
    </div>
  );
};
